import React from "react";
import clsx from "clsx";
import { NavigationButton } from "@/components/ui/atoms/MNavigationButton";
import MDialog from "@/components/ui/atoms/MDialog";
import { useSelector } from "react-redux";
import type { RootState } from "@/store";
import MTopBar from "@/components/ui/atoms/MTopBar";
import styles from "./BaseNavigation.module.css";

/**
 * Props for BaseNavigation
 */
export type BaseNavigationProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNavigate: (route: string) => void;
  version?: string;
} & React.PropsWithChildren;

import { useProfileNavigation } from "@/config/navigation/profile-navigation";
import { useAuth } from "@/hooks/useAuth";
import { getMemberInfo } from "@/hooks/useMembers";
import { MButton } from "@/components/ui/atoms/MButtons";
import FormHeader from "@/components/ui/organisms/MHeader";
import { MTourTrigger } from "@/components/ui/organisms/MTourTrigger";
import { MHelpIcon } from "@/components/ui/atoms/MHelpIcon";
import { useNavigationSource } from "@/hooks/useNavigationSource";
import { trackUserDataLoaded } from "@/utils/gtm";
import {
  ANALYTICS_SCREEN_TYPES,
  ANALYTICS_SCREEN_NAMES,
} from "@/constants/analytics.constants";
import { requestNativeAppVersion } from "@/utils/nativeBridge";

export const BaseNavigation: React.FC<BaseNavigationProps> = ({
  open,
  onNavigate,
  version = "1.0",
  children,
}) => {
  const [showLogoutDialog, setShowLogoutDialog] = React.useState(false);
  const [nativeVersion, setNativeVersion] = React.useState<string | null>(null);
  const tourTriggerRef = React.useRef<any>(null);

  // When the profile drawer opens inside the native WebView, ask the host
  // for the installed binary version (CFBundleShortVersionString / versionName).
  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    requestNativeAppVersion().then((res) => {
      if (!cancelled && res?.version) setNativeVersion(res.version);
    });
    return () => {
      cancelled = true;
    };
  }, [open]);
  const { logout } = useAuth();
  const { member, selectedMember } = useSelector((state: RootState) => ({
    member: state.auth?.member,
    selectedMember: state.auth?.selectedMember,
  }));
  const memberInfo = getMemberInfo(); // Don't ignore selectedMember to get correct onBehalf value
  const { navigateBack } = useNavigationSource();

  const { basicNav } = useProfileNavigation();

  // Track GTM user_data_loaded event for profile page
  React.useEffect(() => {
    if (!memberInfo?.id || !open) return;

    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.USER_PROFILE,
      screenName: ANALYTICS_SCREEN_NAMES.PROFILE,
    });
  }, [open, memberInfo?.id]);

  // Clear selected member on mount to reset to logged-in user
  // COMMENTED OUT: Preserve selected member when navigating to profile landing
  // React.useEffect(() => {
  //   if (selectedMember) {
  //     dispatch(clearSelectedMember());
  //   }
  // }, []);

  // Hide certain items if logged-in member is not the selected member
  const filteredNav = React.useMemo(() => {
    if (!memberInfo.onBehalf) {
      // The Settings screen shows only Basic information / App Settings /
      // Privacy & Security / Support & Feedback / Subscription — "Health
      // Profile" lives in basicNav for the desktop ProfileSidebar, but isn't
      // part of this list.
      const base = basicNav.filter((item) => item.id !== "healthProfile");
      // Hide subscription for family members (not family owner)
      if (memberInfo.family_member_type === "fm") {
        return base.filter((item) => item.path !== "/profile/subscription");
      }
      return base;
    }
    // When viewing someone else's profile, only show Basic Information and Health Profile
    const allowedPaths = [
      "/profile/basic-information",
      "/profile/health-profile",
    ];
    return basicNav.filter((item) => allowedPaths.includes(item.path));
  }, [basicNav, memberInfo.onBehalf, memberInfo.family_member_type]);

  return (
    <>
      {/* Persistent Drawer (non-modal) */}
      <div
        className={clsx(
          "inset-y-0 right-0 z-30 bg-[var(--neutral-0)] p-0 transition-transform duration-500 ease-in-out",
          open ? "translate-x-0" : "translate-x-full"
        )}
        style={{ pointerEvents: open ? "auto" : "none" }}
        aria-hidden={!open}
        tabIndex={-1}
      >
        <div className="main-profile flex flex-col h-full bg-white overflow-y-auto">
          {/* Header */}
          <MTopBar
            hideBack
            disableSticky
            hideProfilePicker
            style={{ background: "#FBF6FF" }}
          />

          <div className={clsx("px-[24px]", styles.profileHeader)}>
            <FormHeader
              subHeading="Settings"
              module="profile"
              memberInfo={memberInfo}
              showGradient
              // /profile is never a bottom-nav tab root — always reached by
              // navigating here (e.g. tapping the avatar on the Dashboard) —
              // so Back always shows; navigateBack() falls back to "/" when
              // no navigation source was recorded.
              hideBack={false}
              onBack={navigateBack}
              description="N/A"
              // description={
              //   memberInfo.email
              //     ? memberInfo.email
              //     : memberInfo.mobile
              //       ? `${memberInfo.dialCode || '+91'} ${memberInfo.mobile}`
              //       : "N/A"
              // }
            />
          </div>

          <nav className={clsx("flex flex-col gap-0 px-6", styles.navSection)}>
            {/* Basic navigation */}
            {filteredNav.map((item) => (
              <NavigationButton
                key={item.label}
                leftIcon={item.iconName}
                rightIcon="chevron-right"
                text={item.label}
                data-profile-basic-information={
                  item.path === "/profile/basic-information" ? true : undefined
                }
                data-profile-health-profile={
                  item.path === "/profile/health-profile" ? true : undefined
                }
                onClick={() => onNavigate(item.path)}
              />
            ))}
          </nav>

          <div className="flex flex-col items-center mt-[80px] pb-4 gap-[0px]">
            <MButton
              variant="link"
              size="md"
              className=""
              onClick={() => setShowLogoutDialog(true)}
            >
              Log Out
            </MButton>
            <MDialog
              isOpen={showLogoutDialog}
              onClose={() => setShowLogoutDialog(false)}
              title="Log Out"
              description="Are you sure you want to log out?"
              primaryCtaText="Yes, log out"
              onPrimaryCta={() => {
                setShowLogoutDialog(false);
                logout();
              }}
              secondaryCtaText="Cancel"
              onSecondaryCta={() => setShowLogoutDialog(false)}
            />
            <div className="body-xs-semibold disabled-color">
              version {nativeVersion || version}
            </div>
          </div>
          <MTourTrigger ref={tourTriggerRef} page="profile" />
          {children}
        </div>
      </div>
    </>
  );
};

export default BaseNavigation;
